<template lang="pug">
    layout.contentTransparent(fluid)

        // Title
        title-component(
            :title="$t('managers.list.title')"
            slot="title"
            type="h2"
        )

        template
            v-layout(row wrap justify-space-between)
                v-flex(xs8 sm6 md4 lg3)
                    div.c-form.mr-2

                        // Search field
                        v-text-field(
                            :label="$t('fields.search')"
                            v-model="search"
                            append-icon="search"
                            class="search-input"
                            outline
                        )

                v-flex(xs4 sm6 md4)
                    div.createButtonWrapper

                        // Create Manager button
                        button-component.mx-0.my-0(
                            :action="createManager"
                            color="action"
                            large
                        ) {{ isMobile ? $t('managers.list.createButtonMobile') : $t('managers.list.createButton') }}

            separator-component.overlay.hidden-xs-only

            v-layout(row wrap)
                v-flex(xs12)

                    v-data-table.c-table(
                        v-if="isMobile"
                        :headers-length="6"
                        :items="items"
                        :loading="isLoading"
                        :pagination.sync="pagination"
                        :total-items="total"
                        :rowsPerPageItems="[10]"
                    )
                        template(
                            v-slot:items="props"
                        )
                            tr(@click="props.expanded = !props.expanded")
                                list-mobile(
                                    :item="props.item"
                                    :expanded="props.expanded"
                                    @removed="resetPagination"
                                )

                        template(slot="expand" slot-scope="props")
                            tr
                                td(colspan="6")
                                    expand-card(:item="props.item")

                    // Managers list (table)
                    v-data-table.c-table(
                        v-if="!isMobile"
                        :headers="headers"
                        :items="items"
                        :loading="isLoading"
                        :pagination.sync="pagination"
                        :total-items="total"
                        :rowsPerPageItems="[10]"
                    )
                        template(slot="headerCell" slot-scope="props")
                            div.text-left(v-if="props.header.filter")
                                filter-component(
                                    :items="props.header.filter"
                                    :name="props.header.text"
                                    :callbackFunction="filtersCallback"
                                    :filterName="props.header.value"
                                )

                            span(v-else) {{ `${props.header.text}  ` }}

                        template(slot="items" slot-scope="props")
                            list-item(:item="props.item" @removed="resetPagination")

        controls-mobile(
            v-if="isMobile"
            :sortHandler="handleMobileSort"
            :filterHandler="filtersCallback"
        )
</template>

<script>
    import Layout from '../../Layout/Layout';
    import ListItem from './ListItem';
    import ListMobile from './ListItemMobile';
    import ExpandCard from './ExpandCard';
    import ControlsMobile from './ControlsMobile';
    import managers from '~api/managers';
    import Button from '~components/Button';
    import Filter from '~components/Filter';
    import Separator from '~components/Separator';
    import Title from '~components/Title';

    export default {
        components: {
            Layout,
            ListItem,
            ListMobile,
            ExpandCard,
            ControlsMobile,
            TitleComponent: Title,
            ButtonComponent: Button,
            FilterComponent: Filter,
            SeparatorComponent: Separator
        },
        data() {
            return {
                headers: this.$t('managers.tableHeaders'),
                filters: {
                    role: null,
                    isActive: null
                },
                items: [],
                pagination: {},
                total: 0,
                rowsPerPage: [10],
                search: null,
                timeout: null,
                routeInitialized: false,
                isLoading: false
            };
        },
        computed: {
            // Check screen resolution
            isMobile() {
                return this.$vuetify.breakpoint.smAndDown;
            }
        },

        /**
         * Retrieve managers
         */
        async beforeRouteEnter(to, from, next) {
            // Fetch data from API.
            const { data } = await managers.list();

            next(vm => {
                vm.items = data.items;

                vm.total = data.meta && data.meta.total ? data.meta.total : 0;

                vm.pagination = {
                    page: data.meta && data.meta.page ? data.meta.page : 0,
                    rowsPerPage:
                        data.meta && data.meta.limit ? data.meta.limit : 0
                };

                vm.$nextTick(() => {
                    vm.routeInitialized = true;
                });
            });
        },
        watch: {
            /**
             * Watch search and set pagination param on change
             */
            search(value) {
                clearTimeout(this.timeout);

                this.timeout = setTimeout(() => {
                    this.pagination = {
                        ...this.pagination,
                        search: value
                    };
                }, 500);
            },

            /**
             * Watch pagination and update managers data on change
             */
            pagination: {
                async handler(paginationData) {
                    let paginationBlock = document.querySelector(
                        '.v-datatable__actions__range-controls'
                    );

                    if (!this.routeInitialized) {
                        return;
                    }

                    // Start progress bar.
                    this.isLoading = true;

                    // Collect params.
                    const params = {
                        page: paginationData.page,
                        sortBy: paginationData.sortBy,
                        orderBy: paginationData.descending ? 'desc' : 'asc'
                    };

                    if (
                        paginationData.search &&
                        paginationData.search.length > 0
                    ) {
                        params.search = paginationData.search;
                    }

                    if (this.filters.role !== null) {
                        params.role = this.filters.role;
                    }

                    if (this.filters.isActive !== null) {
                        params.isActive = this.filters.isActive;
                    }

                    // Fetch data from API.
                    const { data } = await managers.list(params);

                    this.items = data.items;
                    this.total =
                        data.meta && data.meta.total ? data.meta.total : 0;

                    if (paginationBlock) {
                        if (this.total <= this.pagination.rowsPerPage) {
                            paginationBlock.style.display = 'none';
                        } else {
                            paginationBlock.style.display = '';
                        }
                    }

                    // Stop progress bar.
                    this.isLoading = false;
                },
                deep: true
            }
        },
        methods: {
            /**
             * Mobile sorting handler
             */
            handleMobileSort(name, e) {
                this.pagination.sortBy = name;
                this.pagination.descending = e;
                this.resetPagination();
            },
            /**
             * Reset pagination.
             */
            resetPagination() {
                this.pagination = { ...this.pagination };
            },

            /**
             * Redirect to Manager create page
             */
            createManager() {
                this.$router.push({ name: 'dashboard.managers.create' });
            },

            /**
             * Get filter param on change filter
             * @param {object} e
             */
            filtersCallback(e) {
                this.filters[e.name] = e.value;
                this.resetPagination();
            },

            /**
             * Change sort items
             * @param column
             */
            changeSort(column) {
                if (this.pagination.sortBy === column) {
                    this.pagination.descending = !this.pagination.descending;
                } else {
                    this.pagination.sortBy = column;
                    this.pagination.descending = false;
                }
            }
        }
    };
</script>

<style lang="stylus" scoped>
    @import '../../../../assets/style/_variables.styl'

    .createButtonWrapper
        display: flex
        justify-content: flex-end

    .text-left
        text-align: left

    >>> .actions
        min-width: 90px

    @media screen and (max-width: $grid-breakpoints.sm - 1)
        .createButtonWrapper
            .btn
                width: 100%
</style>
